import { useCallback, useMemo, useState, ChangeEvent } from 'react';
import { Link, IconButton, Box, Button, TablePagination } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useLanguage } from '../../../../context/LanguageContext';
import { useOptimisticStore } from '../../../../context/OptimisticStoreContext';
import { useStoreData } from '../../../../context/StoreDataContext';
import { Store, Url } from '../../../../types/Store';
import StoreCard from './common/StoreCard';
import StoreTable from './common/StoreTable';
import { UrlModal, DeleteConfirmModal } from '../../UpdateStore/components/Drawers';
import useDeleteUrl from '../../../../hooks/Magasins/useDeleteUrl';
import { toast } from 'react-toastify';

interface UrlsProps {
    store: Store;
    isEditable?: boolean;
}

function Urls({ store, isEditable = false }: UrlsProps) {
    const { t } = useLanguage();
    const { deleteUrl } = useDeleteUrl();
    const { urlTypes } = useStoreData();
    const { displayStore, actualStore, setActualStore } = useOptimisticStore();
    const urls = (displayStore?.URL || store.URL) || [];

    const [urlModalOpen, setUrlModalOpen] = useState(false);
    const [selectedUrl, setSelectedUrl] = useState<Url | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [urlToDelete, setUrlToDelete] = useState<Url | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedUrls = useMemo(() => {
        const start = page * rowsPerPage;
        return urls.slice(start, start + rowsPerPage);
    }, [urls, page, rowsPerPage]);

    const getUrlTypeLabel = useCallback((urlTypeName: string) => {
        const translationKey = `pages.store.urlTypes.${urlTypeName}`;
        const translatedLabel = t(translationKey);

        return translatedLabel === translationKey ? urlTypeName : translatedLabel;
    }, [t]);

    const urlTypeLabels = useMemo(() => (
        urlTypes.reduce<Record<string, string>>((acc, type) => {
            acc[type.id.toString()] = getUrlTypeLabel(type.name);
            return acc;
        }, {})
    ), [urlTypes, getUrlTypeLabel]);

    const handleEdit = (url: Url) => {
        setSelectedUrl(url);
        setUrlModalOpen(true);
    };

    const handleAdd = () => {
        setSelectedUrl(null);
        setUrlModalOpen(true);
    };

    const handleDeleteClick = (url: Url) => {
        setUrlToDelete(url);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!urlToDelete || !urlToDelete.ENT_ENTITY_URL_ID) return;

        setDeleteModalOpen(false);
        const urlIdToDelete = urlToDelete.ENT_ENTITY_URL_ID;
        const previousStore = actualStore;
        setUrlToDelete(null);

        if (actualStore) {
            const updatedUrls = (actualStore.URL || []).filter((u: Url) => u.ENT_ENTITY_URL_ID !== urlIdToDelete);
            setActualStore({ ...actualStore, URL: updatedUrls });
        }

        const success = await deleteUrl(urlIdToDelete);
        
        if (success) {
            toast.success(t('pages.store.success.urlDeleted'));
        } else {
            if (previousStore) setActualStore(previousStore);
            toast.error(t('pages.store.errors.urlDeleteFailed'));
        }
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                label: t('pages.store.type'),
                render: (url: Url) => (url.URL_TYPE_ID ? urlTypeLabels[url.URL_TYPE_ID.toString()] || url.URL_TYPE_ID : '-') || '-',
            },
            {
                label: t('pages.store.url'),
                render: (url: Url) => url.URL ? (
                    <Link href={url.URL} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all' }}>
                        {url.URL}
                    </Link>
                ) : '-',
            },
        ];

        if (isEditable) {
            baseColumns.push({
                label: t('common.actions'),
                render: (url: Url) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" onClick={() => handleEdit(url)} color="primary">
                            <ModeEditOutlineIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteClick(url)} color="error">
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ),
            });
        }

        return baseColumns;
    }, [t, isEditable, urlTypeLabels]);

    return (
        <>
            <StoreCard
                title={t('pages.store.urls')}
                icon={<LinkIcon sx={{ color: 'primary.main' }} />}
            >
                {urls.length > 0 && (
                    <>
                        <StoreTable columns={columns} data={paginatedUrls} />
                        <TablePagination
                            component="div"
                            count={urls.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25]}
                            labelRowsPerPage={t('common.rowsPerPage')}
                        />
                    </>
                )}
                {urls.length===0 && <div style={{ textAlign: 'center', padding: '16px' }}>{t('pages.store.noUrls')}</div>}
                {isEditable && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button 
                            variant="text" 
                            size="small"
                            startIcon={<AddIcon />} 
                            onClick={handleAdd}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('pages.store.addUrl')}
                        </Button>
                    </Box>
                )}
            </StoreCard>

            <UrlModal
                open={urlModalOpen}
                onClose={() => {
                    setUrlModalOpen(false);
                    setSelectedUrl(null);
                }}
                entityId={store.ENTITY_ID}
                url={selectedUrl}
            />

            <DeleteConfirmModal
                open={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setUrlToDelete(null);
                }}
                onConfirm={handleDeleteConfirm}
                title={t('pages.store.deleteUrl')}
                message={t('pages.store.confirmDeleteUrl')}
            />
        </>
    );
};

export default Urls;
